import type { TFunction } from 'i18next';
import { z } from 'zod';
import type { GetStationResponse } from '@/api/generated/schemas';

/** UI ref destinace ve formuláři (drží bohatý objekt kvůli chipu). */
const destinationRefSchema = z.object({
  destinationId: z.number(),
  name: z.string(),
  code: z.string().nullable(),
});

/** UI ref země. */
const countryRefSchema = z.object({
  countryId: z.number(),
  name: z.string(),
  isoCode: z.string().nullable(),
});

/** UI ref typu stanice (položka číselníku StationType). */
const codebookRefSchema = z.object({
  id: z.number(),
  name: z.string(),
  code: z.string().nullable(),
});

/** Stabilní lokální klíč nového nástupního místa (jen UI — pro React seznam a drag). */
export function newStationDetailKey(): string {
  return `new-${crypto.randomUUID()}`;
}

function buildStationDetailSchema(t: TFunction) {
  return z.object({
    key: z.string(),
    id: z.number().nullable(),
    description: z.string().trim().min(1, t('stationDetail.validation.descriptionRequired')),
    street: z.string(),
    descriptiveNumber: z.string(),
    orientationNumber: z.string(),
    poBox: z.string(),
    city: z.string(),
    zipCode: z.string(),
    country: countryRefSchema.nullable(),
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
    zoom: z.number().min(1).max(18).nullable(),
  });
}

/**
 * Factory Zod schématu detailu stanice. Lokalizované hlášky se vkládají přímo do schématu, proto se
 * staví z `t` až za běhu. Typ hodnot se odvozuje ze schématu (`StationFormValues`).
 */
export function buildStationFormSchema(t: TFunction) {
  return z
    .object({
      name: z.string().trim().min(1, t('station.validation.nameRequired')),
      code: z.string(),
      // Povinnost typu se kontroluje object-level (superRefine), aby zůstal typ hodnoty `ref | null`.
      stationType: codebookRefSchema.nullable(),
      destination: destinationRefSchema.nullable(),
      isDomestic: z.boolean(),
      timeZoneOffset: z.string().nullable(),
      stationDetails: z.array(buildStationDetailSchema(t)),
      // Vlastní souřadnice stanice — platí jen dokud nemá žádné nástupní místo (§6.2).
      stationLatitude: z.number().nullable(),
      stationLongitude: z.number().nullable(),
      stationZoom: z.number().min(1).max(18).nullable(),
    })
    .superRefine((values, ctx) => {
      if (values.stationType == null) {
        ctx.addIssue({
          code: 'custom',
          path: ['stationType'],
          message: t('station.validation.typeRequired'),
        });
      }
    });
}

export type StationFormValues = z.infer<ReturnType<typeof buildStationFormSchema>>;
export type StationDetailValue = StationFormValues['stationDetails'][number];

/** Prázdné výchozí hodnoty pro režim zakládání (Druh = Nástupní). */
export function emptyStationFormValues(): StationFormValues {
  return {
    name: '',
    code: '',
    stationType: null,
    destination: null,
    isDomestic: true,
    timeZoneOffset: null,
    stationDetails: [],
    stationLatitude: null,
    stationLongitude: null,
    stationZoom: null,
  };
}

/** Mapuje detail stanice z API na hodnoty formuláře (režim editace). */
export function fromGetStationResponse(response: GetStationResponse): StationFormValues {
  return {
    name: response.name,
    code: response.code ?? '',
    stationType: { id: response.stationTypeCbId, name: response.typeName ?? '', code: null },
    destination:
      response.destinationId != null
        ? {
            destinationId: response.destinationId,
            name: response.destinationName ?? '',
            code: null,
          }
        : null,
    isDomestic: response.isDomestic,
    timeZoneOffset: response.timeZoneOffset ?? null,
    stationDetails: response.stationDetails.map((detail) => ({
      key: `srv-${detail.id}`,
      id: detail.id,
      description: detail.description,
      street: detail.street ?? '',
      descriptiveNumber: detail.descriptiveNumber ?? '',
      orientationNumber: detail.orientationNumber ?? '',
      poBox: detail.poBox ?? '',
      city: detail.city ?? '',
      zipCode: detail.zipCode ?? '',
      country:
        detail.countryId != null
          ? { countryId: detail.countryId, name: detail.countryName ?? '', isoCode: null }
          : null,
      latitude: detail.latitude ?? null,
      longitude: detail.longitude ?? null,
      zoom: detail.zoom ?? null,
    })),
    stationLatitude: response.latitude ?? null,
    stationLongitude: response.longitude ?? null,
    stationZoom: response.zoom ?? null,
  };
}
